import React, { useState, useEffect, useRef } from "react";
import { useReactToPrint } from "react-to-print";
import classes from "./Reportinvoice.module.css";
import HeaderNav from "../../Components/Dahboard Header/HeaderNav/HeaderNav";
import Footer from "../../Components/Footer/Footer";
import { useLocation, useNavigate } from "react-router-dom";
import api, { BASE_URL } from "../../Components/API/Api";
import {
  BarChart2,
  Filter,
  CalendarClock,
  Mail,
  ArrowLeft,
  Printer,
  Download,
  Settings,
} from "lucide-react";

const BalanceSheetReport = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const invoice = location.state?.invoice;
  const invoiceTitleText = location.state?.invoiceType || 'INVOICE';
  const [companyInfo, setCompanyInfo] = useState(null);
  const componentRef = useRef(null);

  const handlePrint = useReactToPrint({
    contentRef: componentRef,
    documentTitle: invoiceTitleText || "Invoice",
    copyStyles: true,
    pageStyle: `
      @page { margin: 12mm; size: auto; }
      html, body { height: auto !important; background: #ffffff !important; margin: 0; padding: 0; }
      body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      #invoice-print-root { display: block !important; visibility: visible !important; position: relative !important; width: 100% !important; min-height: 100vh !important; background: #fff !important; color: #333 !important; padding: 12mm !important; }
      #invoice-print-root * { visibility: visible !important; }
    `,
  });

  const {
    invoice_number: invoicenumber,
    transaction_date: invoiceDate,
    due_date: dueDate,
    customer,
    items,
    amount: total,
  } = invoice || {};

  // Purchase invoices come with `supplier`, sales invoices with `customer`.
  // Normalize to a single "billTo" object for display.
  const billTo =
    invoice?.customer ||
    invoice?.supplier ||
    invoice?.vendor ||
    invoice?.beneficiary ||
    null;


  const pageMap = {
    "/dashboard": "Dashboard",
    "/transactions": "Transactions",
    "/accounts": "Accounting",
    "/reports": "Reports",
    "/banking": "Banking",
    "/settings": "Settings",
  };

  // Fetch company info from API
  useEffect(() => {
    const fetchCompanyInfo = async () => {
      const token = localStorage.getItem("authToken") || localStorage.getItem("token");
      if (!token) return;

      try {
        const res = await api.get(`/v1/companies/get-info`, {
          headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
        });

        // Handle the API response structure: data.data[0]
        const item = Array.isArray(res?.data?.data)
          ? res.data.data[0]
          : res?.data?.data || null;

        if (item) {
          setCompanyInfo({
            name: item.name || "",
            address: item.address || "",
            email: item.email || "",
            phone: item.phone_number || "",
            logo: item.logo || "",
            bank_name: item.bank_name || "",
            account_name: item.account_name || "",
            account_number: item.account_number || "",
          });
        }
      } catch (err) {
        console.error("Failed to fetch company info", err);
      }
    };

    fetchCompanyInfo();
  }, []);

  if (!invoice) {
    return (
      <div style={{ padding: "2rem", textAlign: "center" }}>
        <h2>No invoice data found.</h2>
        <button onClick={() => navigate(-1)}>Go Back</button>
      </div>
    );
  }

  const printInvoice = () => {
    if (handlePrint) {
      handlePrint();
    }
  };

  const safeNumber = (v) => {
    const n = Number(v ?? 0);
    return Number.isFinite(n) ? n : 0;
  };

  const hasSalesArray = Array.isArray(invoice?.sales) && invoice.sales.length > 0;

  const getItemDescription = (sale) => {
    const itemDescription =
      sale?.description ||
      sale?.item?.description ||
      sale?.item?.name ||
      sale?.item_name ||
      sale?.name ||
      "—";

    return String(itemDescription).trim() || "—";
  };

  const subtotal = hasSalesArray
    ? invoice.sales.reduce((sum, sale) => {
        // try common numeric keys on a sale line
        return (
          sum +
          safeNumber(
            sale.amount ?? sale.total ?? sale.price ?? sale.line_total ?? sale.sub_total ?? sale.balance
          )
        );
      }, 0)
    : safeNumber(
        invoice?.sub_total ?? invoice?.subtotal ?? invoice?.amount ?? invoice?.balance ?? invoice?.total_price ?? invoice?.amount_sold
      );

  const vatAmount = safeNumber(invoice?.total_vat ?? invoice?.vat ?? invoice?.vat_amount);
  const discountAmount = safeNumber(invoice?.total_discount ?? invoice?.discount ?? invoice?.discount_amount);
  const whtAmount = safeNumber(invoice?.total_withholding_tax ?? invoice?.withholding_tax ?? invoice?.total_wht ?? invoice?.wht ?? invoice?.withholding ?? invoice?.wht_amount);
  const totalAmount = safeNumber(invoice?.amount_sold ?? invoice?.amount ?? invoice?.balance ?? invoice?.total_price ?? subtotal);

  const isPaid =
    (invoice?.status || invoice?.payment_status || "")
      .toString()
      .toLowerCase() === "paid";
  const statusLabel = isPaid ? "PAID" : "UNPAID";

  // Use fetched company info or fallback to invoice data or defaults
  const companyName =
    companyInfo?.name ||
    invoice?.company?.name ||
    invoice?.business?.name ||
    invoice?.organization?.name ||
    invoice?.company_name ||
    invoice?.business_name ||
    "Brookes Professional Services Limited";

  const companyAddress =
    companyInfo?.address ||
    invoice?.company?.address ||
    invoice?.business?.address ||
    invoice?.organization?.address ||
    invoice?.company_address ||
    "12 Adeola Street, Ikeja, Lagos";

  const companyPhone =
    companyInfo?.phone ||
    invoice?.company?.phone_number ||
    invoice?.company?.phone ||
    invoice?.business?.phone_number ||
    invoice?.business?.phone ||
    invoice?.company_phone ||
    "0800 000 0000";

  const companyEmail =
    companyInfo?.email ||
    invoice?.company?.email ||
    invoice?.business?.email ||
    invoice?.organization?.email ||
    invoice?.company_email ||
    "info@brookespsl.com";

  const companyLogo =
    companyInfo?.logo ||
    invoice?.company?.logo ||
    invoice?.business?.logo ||
    invoice?.organization?.logo ||
    invoice?.company_logo ||
    "/logo.png";

  const companyBankName = companyInfo?.bank_name || invoice?.company?.bank_name || "—";
  const companyAccountName = companyInfo?.account_name || invoice?.company?.account_name || companyName || "—";
  const companyAccountNumber = companyInfo?.account_number || invoice?.company?.account_number || "—";

  return (
    <div>
      <div className={classes.noPrint}>
        <HeaderNav activePage={pageMap[location.pathname]} />
      </div>

      <div className={classes.Container}>

        <div className={`${classes.actions} ${classes.noPrint}`}>
          <button className={classes.btns}>New Sales Invoice</button>
          <button className={classes.btns}>New Purchase Invoice</button>
          <button className={classes.btns}>All Invoices</button>
          <button className={`${classes.btns} ${classes.greens}`}>
            Product & Services
          </button>
        </div>

        <div className={classes.act}>
          <form className={classes.form}>
            <div className={classes.group}>
              <label>Period:</label>
              <div className={classes.dropdownWrapper}>
                <select className={classes.statusDropdown}>
                  <option>Current month</option>
                  <option>Cash</option>
                  <option>Cheque</option>
                  <option>Transfer</option>
                </select>
                <span className={classes.arrowDown}>▼</span>
              </div>
            </div>

            <div className={classes.group1}>
              <label>Group by:</label>
              <div className={classes.dropdownWrapper}>
                <select className={classes.statusDropdown}>
                  <option>None</option>
                  <option>Cash</option>
                  <option>Cheque</option>
                  <option>Transfer</option>
                </select>
                <span className={classes.arrowDown}>▼</span>
              </div>
            </div>
            <div className={classes.group1}>
              <label>Start Date:</label>
              <div className={classes.dropdownWrapper}>
                <select className={classes.statusDropdown}>
                  <option>30/02/25</option>
                  <option>12/07/25</option>
                  <option>03/04/25</option>
                  <option>27/01/25</option>
                </select>
                <span className={classes.arrowDown}>▼</span>
              </div>
            </div>
            <div className={classes.group1}>
              <label>End Date:</label>
              <div className={classes.dropdownWrapper}>
                <select className={classes.statusDropdown}>
                  <option>30/02/25</option>
                  <option>12/07/25</option>
                  <option>03/04/25</option>
                  <option>27/01/25</option>
                </select>
                <span className={classes.arrowDown}>▼</span>
              </div>
            </div>

            <div className={classes.group1}>
              <button className={classes.greenBtn}>
                <BarChart2 size={16} style={{ marginRight: "6px" }} />
                Run Report
              </button>
            </div>
          </form>

          <div className={classes.iconbars}>
            <div className={classes.iconbarr}>
              <div className={classes.iconbar}>
                <Filter size={16} style={{ marginRight: "6px" }} />
                Filter
              </div>
              <div className={classes.iconbar}>
                <CalendarClock size={16} style={{ marginRight: "6px" }} />
                Schedule
              </div>
            </div>

            <div className={classes.iconbarss}>
              <div className={classes.iconbar}>
                <Mail size={16} />
              </div>
              <div className={classes.iconbar}>
                <Printer size={16} onClick={printInvoice} />
              </div>
              <div className={classes.iconbar}>
                <Download size={16} />
              </div>
              <div className={classes.iconbar}>
                <Settings size={16} />
              </div>
            </div>
          </div>
        </div>

        <div className={`${classes.matron} ${classes.noPrint}`}>
          <button
            className={classes.backButton}
            onClick={() => navigate(-1)}
          >
            <ArrowLeft size={16} style={{ marginRight: "6px" }} />
          </button>
        </div>
        <div
          id="invoice-print-root"
          ref={componentRef}
          className={`${classes.containere} ${classes.printable}`}
        >
          <div className={classes.invoiceBox}>
            {/* <div
              className={`${classes.statusBadge} ${isPaid ? classes.statusBadgePaid : classes.statusBadgeUnpaid
                }`}
            >
              {statusLabel}
            </div> */}

            <div className={classes.watermark}>
              {statusLabel}
            </div>

            <div className={classes.invoiceHeader}>
              <div className={classes.company}>
                <img
                  src={companyLogo}
                  alt="Company Logo"
                  className={classes.companyLogo}
                />
                <div>
                  <h2>{companyName}</h2>
                  <small>
                    {companyAddress}
                    <br />
                    {companyPhone} | {companyEmail}
                  </small>
                </div>
              </div>

              <div className={classes.invoiceTitle}>
                <h1>{invoiceTitleText}</h1>
                <p>
                  Invoice No: {invoice?.invoice_number || "—"}
                  <br />
                  Date: {invoice?.transaction_date || "—"}
                  <br />
                  Due Date: {invoice?.due_date || "—"}
                </p>
              </div>
            </div>

            <table className={classes.infoTable}>
              <tbody>
                <tr>
                  <td>
                    <strong>BILL TO</strong>
                    <br />
                    {billTo?.name || "—"}
                    <br />
                    {billTo?.address || ""}
                    {(billTo?.phone_number || billTo?.phone) && (
                      <>
                        <br />
                        {billTo.phone_number || billTo.phone}
                      </>
                    )}
                    {billTo?.email && (
                      <>
                        <br />
                        {billTo.email}
                      </>
                    )}
                  </td>
                  <td>
                    <strong>Payment Terms</strong>
                    <br />
                    {invoice?.terms || "7 Days"}
                  </td>
                </tr>
              </tbody>
            </table>

            <table className={classes.itemsTable}>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Description</th>
                  <th>Qty</th>
                  <th>Unit Price (₦)</th>
                  <th>Amount (₦)</th>
                </tr>
              </thead>
              <tbody>
                {hasSalesArray ? (
                  invoice.sales.map((sale, index) => {
                    const isService = Boolean(
                      // explicit flags/types on the item
                      (sale?.item?.type && String(sale.item.type).toLowerCase().includes("service")) ||
                      (sale?.item_type && String(sale.item_type).toLowerCase().includes("service")) ||
                      sale?.is_service === true ||
                      sale?.service === true ||
                      // invoice-level indicator (API uses "type": "1" for service invoices in some responses)
                      invoice?.type === "1" || invoice?.type === 1 ||
                      // invoice or sale description mentioning service
                      String(invoice?.description || "").toLowerCase().includes("service") ||
                      String(sale?.description || "").toLowerCase().includes("service") ||
                      // fallback: item is null but item_id is a non-numeric string (likely a service name/id)
                      (sale?.item == null && typeof sale?.item_id === "string" && isNaN(Number(sale.item_id)))
                    );

                    const description = getItemDescription(sale);

                    const quantity = Number(sale.quantity || 0);
                    const lineAmount = safeNumber(sale.amount ?? sale.total ?? sale.price ?? sale.line_total ?? sale.balance);
                    const unitRate =
                      quantity > 0
                        ? lineAmount / quantity
                        : Number(sale.item?.price || sale.unit_price || 0);

                    return (
                      <tr key={index}>
                        <td>{index + 1}</td>
                        <td>{description}</td>
                        <td>{quantity.toLocaleString()}</td>
                        <td>
                          {/* ₦ */}
                          {unitRate.toLocaleString("en-NG", {
                            minimumFractionDigits: 2,
                          })}
                        </td>
                        <td>
                          {/* ₦ */}
                          {lineAmount.toLocaleString("en-NG", {
                            minimumFractionDigits: 2,
                          })}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td>1</td>
                    <td>{invoice?.description || invoice?.notes || "—"}</td>
                    <td>1</td>
                    <td>
                      ₦
                      {subtotal.toLocaleString("en-NG", {
                        minimumFractionDigits: 2,
                      })}
                    </td>
                    <td>
                      ₦
                      {totalAmount.toLocaleString("en-NG", {
                        minimumFractionDigits: 2,
                      })}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            <div className={classes.totals}>
              <table>
                <tbody>
                  <tr>
                    <td>Subtotal</td>
                    <td style={{textAlign:"right"}}>
                      {/* ₦ */}
                      {subtotal.toLocaleString("en-NG", {
                        minimumFractionDigits: 2,
                        
                      })}
                    </td>
                  </tr>
                  <tr>
                    <td>VAT (7.5%)</td>
                    <td style={{textAlign:"right"}}>
                      {/* ₦ */}
                      {vatAmount.toLocaleString("en-NG", {
                        minimumFractionDigits: 2,
                      })}
                    </td>
                  </tr>
                  <tr>
                    <td>Withholding Tax (WHT)</td>
                    <td style={{textAlign:"right"}}>
                      {whtAmount.toLocaleString("en-NG", {
                        minimumFractionDigits: 2,
                      })}
                    </td>
                  </tr>
                  <tr>
                    <td>Discount</td>
                    <td style={{textAlign:"right"}}>
                      {discountAmount > 0 ? "-" : ""}
                      {/* ₦ */}
                      {Math.abs(discountAmount).toLocaleString("en-NG", {
                        minimumFractionDigits: 2,
                      })}
                    </td>
                  </tr>
                  <tr>
                    <td>Total</td>
                    <td style={{textAlign:"right"}}>
                      {/* ₦ */}
                      {totalAmount.toLocaleString("en-NG", {
                        minimumFractionDigits: 2,
                      })}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className={classes.clear} />

            <div className={classes.section}>
              <strong>Payment Details</strong>
              <br />
              {companyBankName}
              <br />
              {companyAccountName}
              <br />
              {companyAccountNumber}
            </div>

            <div className={classes.section}>
              <strong>Notes</strong>
              <br />
              Please make payment on or before the due date.
              <br />
              Thank you for your business.
            </div>

            <div className={classes.signature}>
              ___________________________
              <br />
              Authorized Signature
            </div>

            <div className={classes.printFooter}>
              Created by
              <br />
              <a
                href="https://promixaccounting.com"
                target="_blank"
                rel="noreferrer"
              >
                https://promixaccounting.com
              </a>
            </div>
          </div>
        </div>
      </div>

      <div className={classes.noPrint}>
        <Footer />
      </div>
    </div >
  );
};

export default BalanceSheetReport;